import { Briefcase, Plus, Search } from 'lucide-react'
import { useDeferredValue, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { usePagination } from '@/components/common/usePagination'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ConfirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { HR_POSITION_WRITE_PERM } from '@/features/hr/permissions'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useDeletePosition, useDepartments, usePositions } from './api'
import { PositionDetailPanel } from './components/PositionDetailPanel'
import { PositionFormDialog } from './components/PositionFormDialog'
import { PositionsTable } from './components/PositionsTable'
import type { Position } from './types'
import { toastError } from './ui'

export function PositionsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const remove = useDeletePosition()
  const departments = useDepartments()
  const [q, setQ] = useState('')
  const search = useDeferredValue(q.trim().toLocaleLowerCase('tr'))
  const [departmentId, setDepartmentId] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dialog, setDialog] = useState<{ open: boolean; position: Position | null }>({ open: false, position: null })
  const [pendingDelete, setPendingDelete] = useState<Position | null>(null)
  const [forceDelete, setForceDelete] = useState(false)
  const canWrite = can(HR_POSITION_WRITE_PERM)
  const positions = usePositions()
  const all = positions.data
  const filtered = useMemo(
    () =>
      (all ?? []).filter(
        (p) =>
          (!departmentId || p.departmentId === departmentId) &&
          (!search || `${p.title} ${p.code} ${p.department?.name ?? ''}`.toLocaleLowerCase('tr').includes(search)),
      ),
    [all, departmentId, search],
  )
  const { pageRows: rows, pager } = usePagination(filtered, [search, departmentId])
  const total = all?.length ?? 0
  const selected = useMemo(() => rows.find((p) => p.id === selectedId) ?? rows[0] ?? null, [rows, selectedId])

  const confirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await remove.mutateAsync({ id: pendingDelete.id, confirm: forceDelete })
      toast.success(t('access.positions.deleted'))
      if (selectedId === pendingDelete.id) setSelectedId(null)
      setPendingDelete(null)
      setForceDelete(false)
    } catch (e) {
      if (isApiError(e) && e.status === 409 && !forceDelete) {
        setForceDelete(true)
        toast.message(e.message || t('access.positions.deleteWarn'))
        return
      }
      setPendingDelete(null)
      setForceDelete(false)
      toastError(e)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.hr')}
        title={t('access.positions.title')}
        description={t('access.positions.subtitle')}
        actions={
          canWrite ? (
            <Button onClick={() => setDialog({ open: true, position: null })}>
              <Plus />
              {t('access.positions.create')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-4">
        <section className="overflow-hidden rounded-2xl border bg-card">
          <div className="flex flex-wrap items-center gap-2 border-b p-3">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('access.positions.searchPlaceholder')} aria-label={t('access.positions.searchPlaceholder')} className="pl-8" />
            </div>
            <Select
              className="w-48"
              value={departmentId || 'all'}
              onChange={(v) => setDepartmentId(v === 'all' ? '' : v)}
              aria-label={t('access.positions.columns.department')}
              options={[
                { value: 'all', label: t('access.positions.allDepartments') },
                ...(departments.data ?? []).map((d) => ({ value: d.id, label: d.name })),
              ]}
            />
            <span className="ml-auto text-xs text-muted-foreground">{t('access.positions.count', { count: filtered.length, total })}</span>
          </div>
          {positions.isError ? (
            <ErrorState error={positions.error} onRetry={() => void positions.refetch()} />
          ) : (
            <>
              <PositionsTable
                rows={rows}
                loading={positions.isLoading}
                selectedId={selected?.id ?? null}
                canWrite={canWrite}
                onSelect={(p) => setSelectedId(p.id)}
                onEdit={(p) => setDialog({ open: true, position: p })}
                onDelete={(p) => {
                  setForceDelete(false)
                  setPendingDelete(p)
                }}
              />
              {!positions.isLoading && filtered.length === 0 ? (
                <EmptyState icon={Briefcase} title={total ? t('access.positions.noMatch') : t('access.positions.empty')} />
              ) : null}
              <Pager {...pager} />
            </>
          )}
        </section>

        {selected ? <PositionDetailPanel position={selected} canWrite={canWrite} onEdit={() => setDialog({ open: true, position: selected })} /> : null}
      </div>

      <PositionFormDialog open={dialog.open} position={dialog.position} onOpenChange={(open) => setDialog((s) => ({ ...s, open }))} />
      <ConfirmModal
        open={!!pendingDelete}
        onOpenChange={(v) => {
          if (!v) {
            setPendingDelete(null)
            setForceDelete(false)
          }
        }}
        title={t('access.positions.deleteTitle')}
        description={
          forceDelete
            ? t('access.positions.deleteForceBody', { name: pendingDelete?.title ?? '' })
            : t('access.positions.deleteBody', { name: pendingDelete?.title ?? '' })
        }
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  )
}
